import { MessageProvider } from '@core/context/messageContext';
import { HttpClient } from '@core/httpClient';
import { LOCAL_TRANSLATIONS } from '@core/i18n/constants';
import { datadogRum } from '@datadog/browser-rum';
import { AmplitudeTracker } from '@services/amplitude/amplitudeTracker';
import { ErrorBoundary } from '@services/ErrorBoundary';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SplashScreenPage } from '@pages/SplashScreen';

import AppRoutes from './routes';

const App: React.FC = () => {
  const { i18n } = useTranslation();

  const [isClientReady, setIsClientReady] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      const browserLang = navigator.language || 'en';
      const langCode = browserLang.split('-')[0];
      const urlParams = new URLSearchParams(window.location.search);
      const encodedData = urlParams.get('data');

      HttpClient.setAppDataRequest(encodedData || '');

      HttpClient.init({
        appName: 'ap-ck',
        baseURL: import.meta.env.VITE_API_BASE_URL,
        timeout: 5_000,
      });
      AmplitudeTracker.init();

      if (Object.keys(LOCAL_TRANSLATIONS).includes(langCode)) {
        await i18n.changeLanguage(langCode);
      } else {
        await i18n.changeLanguage('en');
      }

      datadogRum.init({
        applicationId: import.meta.env.VITE_DATA_DOG_APPLICATION_ID,
        clientToken: import.meta.env.VITE_DATA_DOG_CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: 'checkout',
        env: import.meta.env.VITE_ENV,
        version: import.meta.env.VITE_VERSION,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        sessionSampleRate: 90,
        sessionReplaySampleRate: 80, // set the % of the sample rate to record
        defaultPrivacyLevel: 'mask-user-input',
      });

      setIsClientReady(true);
    };

    initializeApp();
  }, [i18n]);

  if (!isClientReady) {
    return <SplashScreenPage />;
  }

  return (
    <ErrorBoundary>
      <div className="root font-matter h-full">
        <MessageProvider>
          <AppRoutes />
        </MessageProvider>
      </div>
    </ErrorBoundary>
  );
};

export default App;
